import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config';

const QuoteCreate = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Quote configuration
  const [quoteConfig, setQuoteConfig] = useState({
    title: '',
    validUntil: '',
    notes: '',
    includeDiscount: true,
    showIndividualPrices: true,
    template: 'standard'
  });

  // Multiple options state
  const [multipleOptionsEnabled, setMultipleOptionsEnabled] = useState(false);
  const [options, setOptions] = useState([]);

  // Stationery settings for calculations
  const [stationerySettings, setStationerySettings] = useState({
    marginPercentage: 25,
    artworkFee: 45,
    setupFee: 25,
    gstPercentage: 10
  });

  // Fetch stationery settings for calculations
  useEffect(() => {
    if (order?.orderType === 'stationery') {
      const fetchStationerySettings = async () => {
        try {
          const token = await getToken();
          const response = await fetch(`${config.apiUrl}/api/settings`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              setStationerySettings({
                marginPercentage: parseFloat(data.data.stationeryMarginPercentage) || 25,
                artworkFee: parseFloat(data.data.stationeryArtworkFee) || 45,
                setupFee: parseFloat(data.data.stationerySetupFee) || 25,
                gstPercentage: parseFloat(data.data.stationeryGstPercentage) || 10
              });
            }
          }
        } catch (error) {
          console.error('Error fetching stationery settings:', error);
        }
      };
      fetchStationerySettings();
    }
  }, [order, getToken]);

  // Calculate total price for an option
  const calculateOptionTotalPrice = (option) => {
    const price = parseFloat(option.price) || 0;
    const freightPrice = parseFloat(option.freightPrice) || 0;
    const additionalArtworkHours = parseFloat(option.additionalArtworkHours) || 0;
    
    // Calculate margin (25% of price)
    const margin = price * (stationerySettings.marginPercentage / 100);
    
    // Calculate artwork fee (additional hours * $45 + base $45)
    const artworkFee = stationerySettings.artworkFee + (additionalArtworkHours * stationerySettings.artworkFee);
    
    // Setup fee is fixed
    const setupFee = stationerySettings.setupFee;
    
    // Calculate subtotal before GST and discount
    const subtotalBeforeGst = price + freightPrice + margin + artworkFee + setupFee;
    
    // Calculate GST (10% of subtotal)
    const gst = subtotalBeforeGst * (stationerySettings.gstPercentage / 100);
    
    // Calculate discount
    const discountAmount = parseFloat(option.discountAmount) || 0;
    const discountType = option.discountType || '$';
    let discountValue = 0;
    
    if (discountAmount > 0) {
      if (discountType === '%') {
        discountValue = (subtotalBeforeGst + gst) * (discountAmount / 100);
      } else {
        discountValue = discountAmount;
      }
    }
    
    // Calculate total
    const total = subtotalBeforeGst + gst - discountValue;
    
    return {
      margin: margin.toFixed(2),
      artworkFee: artworkFee.toFixed(2),
      setupFee: setupFee.toFixed(2),
      gst: gst.toFixed(2),
      totalPrice: total.toFixed(2)
    };
  };

  // Initialize first option when multiple options is enabled
  useEffect(() => {
    if (multipleOptionsEnabled && order && options.length === 0) {
      const initialOption = {
        id: 1,
        name: 'Option 1',
        productType: order.productType || '',
        quantity: order.quantity || '',
        size: order.size || '',
        sides: order.sides || '',
        stock: order.stock || '',
        finish: order.finish || '',
        price: order.price || '0.00',
        freightPrice: order.freightPrice || '0.00',
        additionalArtworkHours: order.additionalArtworkHours || '0',
        margin: order.margin || '0.00',
        artworkFee: order.artworkFee || '0.00',
        setupFee: order.setupFee || '0.00',
        gst: order.gst || '0.00',
        discountType: order.discountType || '$',
        discountAmount: order.discountAmount || '0.00',
        totalPrice: order.totalPrice || '0.00'
      };
      setOptions([initialOption]);
    } else if (!multipleOptionsEnabled) {
      setOptions([]);
    }
  }, [multipleOptionsEnabled, order]);

  // Function to add a new option
  const addNewOption = () => {
    if (options.length === 0) return;
    
    const lastOption = options[options.length - 1];
    const newOption = {
      ...lastOption,
      id: options.length + 1,
      name: `Option ${options.length + 1}`
    };
    
    setOptions([...options, newOption]);
  };

  // Function to update an option
  const updateOption = (optionId, field, value) => {
    setOptions(options.map(option => {
      if (option.id === optionId) {
        const updatedOption = { ...option, [field]: value };
        
        // Auto-calculate totals when pricing fields change
        const pricingFields = ['price', 'freightPrice', 'additionalArtworkHours', 'discountAmount', 'discountType'];
        if (pricingFields.includes(field)) {
          const calculations = calculateOptionTotalPrice(updatedOption);
          return {
            ...updatedOption,
            margin: calculations.margin,
            artworkFee: calculations.artworkFee,
            setupFee: calculations.setupFee,
            gst: calculations.gst,
            totalPrice: calculations.totalPrice
          };
        }
        
        return updatedOption;
      }
      return option;
    }));
  };

  // Function to remove an option
  const removeOption = (optionId) => {
    if (options.length <= 1) return; // Don't allow removing the last option
    setOptions(options.filter(option => option.id !== optionId));
  };

  // Function to check for duplicate options
  const validateOptions = () => {
    if (!multipleOptionsEnabled || options.length <= 1) return { isValid: true, message: '' };
    
    const duplicates = [];
    for (let i = 0; i < options.length; i++) {
      for (let j = i + 1; j < options.length; j++) {
        const option1 = options[i];
        const option2 = options[j];
        
        // Compare key fields that make options unique
        if (option1.productType === option2.productType &&
            option1.quantity === option2.quantity &&
            option1.size === option2.size &&
            option1.sides === option2.sides &&
            option1.stock === option2.stock &&
            option1.finish === option2.finish) {
          duplicates.push(`${option1.name} and ${option2.name}`);
        }
      }
    }
    
    if (duplicates.length > 0) {
      return {
        isValid: false,
        message: `Duplicate options detected: ${duplicates.join(', ')}. Please modify the options to make them unique.`
      };
    }
    
    return { isValid: true, message: '' };
  };

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = await getToken();
        let endpoint = '';
        
        // Determine which endpoint to use based on order type detection
        try {
          // Try advertising orders first
          const response = await axios.get(`${config.apiUrl}/api/ad-orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data && response.data.success) {
            const orderData = response.data.data;
            setOrder({ ...orderData, orderType: 'ad' });
            setQuoteConfig(prev => ({
              ...prev,
              title: `Advertising Quote - ${orderData.businessName || orderData.organizationName || 'Order'}`,
              validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 1 week from now
            }));
            return;
          }
        } catch (adError) {
        }

        try {
          // Try school orders
          const response = await axios.get(`${config.apiUrl}/api/school-orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data && response.data.success) {
            const orderData = response.data.data;
            setOrder({ ...orderData, orderType: 'school' });
            setQuoteConfig(prev => ({
              ...prev,
              title: `School Newsletter Quote - ${orderData.businessName || orderData.organizationName || 'Order'}`,
              validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 1 week from now
            }));
            return;
          }
        } catch (schoolError) {
        }

        try {
          // Try stationery orders
          const response = await axios.get(`${config.apiUrl}/api/stationery-orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data && response.data.success) {
            const orderData = response.data.data;
            setOrder({ ...orderData, orderType: 'stationery' });
            setQuoteConfig(prev => ({
              ...prev,
              title: 'Stationery Quote',
              validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 1 week from now
            }));
            return;
          } else {
          }
        } catch (stationeryError) {
        }

        // If none of the above worked
        setError('Order not found or unsupported order type');
        
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId, getToken]);

  const handleConfigChange = (field, value) => {
    setQuoteConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateQuote = async () => {
    // Validate options first if multiple options is enabled
    const validation = validateOptions();
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    setCreating(true);
    try {
      const token = await getToken();
      
      const payload = {
        orderType: order.orderType,
        quoteConfig: quoteConfig
      };

      // Add options data if multiple options is enabled
      if (multipleOptionsEnabled) {
        payload.options = options;
      }
      
      const response = await axios.post(
        `${config.apiUrl}/api/quote/create/${orderId}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.success) {
        const quoteUrl = response.data.quoteUrl;
        
        // Note: Backend automatically updates status to "Quote Active" for stationery orders
        // No need to update status here - let the backend handle it
        
        // Show success dialog with quote URL
        const confirmed = window.confirm(
          `Quote created successfully!\n\n` +
          `You can now share this link with your client:\n${quoteUrl}\n\n` +
          `Would you like to open the quote now?`
        );
        
        if (confirmed) {
          window.open(quoteUrl, '_blank');
        }
        
        // Navigate back based on order type
        if (order?.orderType === 'stationery') {
          navigate('/app/stationery-orders');
        } else {
          navigate('/app/order-workflow');
        }
      } else {
        throw new Error(response.data?.message || 'Failed to create quote');
      }
      
    } catch (err) {
      console.error('Error creating quote:', err);
      
      let errorMessage = 'Failed to create quote';
      
      if (err.response?.status === 404) {
        errorMessage = 'Quote creation endpoint not found. Please ensure the backend service is running.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleBackToWorkflow = () => {
    // Navigate back based on order type
    if (order?.orderType === 'stationery') {
      navigate('/app/stationery-orders');
    } else {
      navigate('/app/order-workflow');
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false}>
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={handleBackToWorkflow} sx={{ mt: 2 }}>
            Back to Order Workflow
          </Button>
        </Box>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth={false}>
        <Box mt={4}>
          <Alert severity="warning">Order not found</Alert>
          <Button onClick={handleBackToWorkflow} sx={{ mt: 2 }}>
            Back to Order Workflow
          </Button>
        </Box>
      </Container>
    );
  }

  const businessName = order?.organizationName || order?.businessName || 'Unknown Business';

  return (
    <Container maxWidth={false}>
      <Box sx={{ mt: 2, mb: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBackToWorkflow} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Generate Quote - {businessName}
          </Typography>
        </Box>

        {/* Order Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Order ID:</strong> {order.id}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Order Type:</strong> {order.orderType?.toUpperCase() || 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Status:</strong> {order.status}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Business Name:</strong> {businessName}
              </Typography>
            </Grid>
            {order.orderType === 'ad' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Total Audiences:</strong> {order.audiences?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Total Price:</strong> ${parseFloat(order.totalPrice || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </>
            )}
            {order.orderType === 'school' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Products:</strong> {order.productsOrdered || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Total Price:</strong> ${parseFloat(order.totalPrice || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </>
            )}
            {order.orderType === 'stationery' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Product Type:</strong> {order.productType || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Quantity:</strong> {order.quantity || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Size:</strong> {order.size || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Sides:</strong> {order.sides === '1' ? 'Single Sided' : order.sides === '2' ? 'Double Sided' : 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Total Price:</strong> ${parseFloat(order.totalPrice || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Multiple Options Toggle - Only for Stationery Orders */}
        {order.orderType === 'stationery' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Quote Options
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={multipleOptionsEnabled}
                    onChange={(e) => setMultipleOptionsEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Multiple Options"
                labelPlacement="start"
              />
            </Box>
            
            {multipleOptionsEnabled && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Multiple options mode allows you to create different quantity/specification options for the quote.
              </Alert>
            )}
          </Paper>
        )}

        {/* Options Container - Only show when multiple options is enabled */}
        {multipleOptionsEnabled && order.orderType === 'stationery' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">
                Quote Options ({options.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addNewOption}
                size="small"
              >
                Add Option
              </Button>
            </Box>
            
            {options.map((option, index) => (
              <Box key={option.id} sx={{ mb: 3 }}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {option.name}
                    </Typography>
                    {options.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeOption(option.id)}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Product Type"
                        value={option.productType}
                        onChange={(e) => updateOption(option.id, 'productType', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Quantity"
                        value={option.quantity}
                        onChange={(e) => updateOption(option.id, 'quantity', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Size"
                        value={option.size}
                        onChange={(e) => updateOption(option.id, 'size', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sides</InputLabel>
                        <Select
                          value={option.sides}
                          onChange={(e) => updateOption(option.id, 'sides', e.target.value)}
                          label="Sides"
                        >
                          <MenuItem value="1">Single Sided</MenuItem>
                          <MenuItem value="2">Double Sided</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Stock"
                        value={option.stock}
                        onChange={(e) => updateOption(option.id, 'stock', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Finish"
                        value={option.finish}
                        onChange={(e) => updateOption(option.id, 'finish', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Price"
                        value={option.price}
                        onChange={(e) => updateOption(option.id, 'price', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Freight Price"
                        value={option.freightPrice}
                        onChange={(e) => updateOption(option.id, 'freightPrice', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Additional Artwork Hours"
                        value={option.additionalArtworkHours}
                        onChange={(e) => updateOption(option.id, 'additionalArtworkHours', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Discount Type</InputLabel>
                        <Select
                          value={option.discountType}
                          onChange={(e) => updateOption(option.id, 'discountType', e.target.value)}
                          label="Discount Type"
                        >
                          <MenuItem value="$">Dollar ($)</MenuItem>
                          <MenuItem value="%">Percentage (%)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Discount Amount"
                        value={option.discountAmount}
                        onChange={(e) => updateOption(option.id, 'discountAmount', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{option.discountType === '%' ? '%' : '$'}</InputAdornment>,
                        }}
                        size="small"
                      />
                    </Grid>
                    
                    {/* Calculated Fields (Read-only) */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="secondary" sx={{ mt: 2, mb: 1 }}>
                        Calculated Values (Read-only)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Margin"
                        value={`$${option.margin}`}
                        InputProps={{ readOnly: true }}
                        variant="filled"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Artwork Fee"
                        value={`$${option.artworkFee}`}
                        InputProps={{ readOnly: true }}
                        variant="filled"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="GST"
                        value={`$${option.gst}`}
                        InputProps={{ readOnly: true }}
                        variant="filled"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Total Price"
                        value={`$${option.totalPrice}`}
                        InputProps={{ readOnly: true }}
                        variant="filled"
                        size="small"
                        sx={{ 
                          '& .MuiFilledInput-root': { 
                            backgroundColor: 'primary.50',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            ))}
          </Paper>
        )}

        {/* Quote Configuration */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quote Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quote Title"
                value={quoteConfig.title}
                onChange={(e) => handleConfigChange('title', e.target.value)}
                helperText="This will appear as the main heading on the quote"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Valid Until"
                value={quoteConfig.validUntil}
                onChange={(e) => handleConfigChange('validUntil', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Quote expiration date"
              />
            </Grid>
            
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Notes"
                value={quoteConfig.notes}
                onChange={(e) => handleConfigChange('notes', e.target.value)}
                placeholder="Add any special terms, conditions, or notes for this quote..."
              />
            </Grid>
          </Grid>
        </Paper>


        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleBackToWorkflow}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={20} /> : <ReceiptIcon />}
            onClick={handleCreateQuote}
            disabled={creating || !quoteConfig.title}
          >
            {creating ? 'Creating Quote...' : 'Create Quote'}
          </Button>
        </Box>

        {!quoteConfig.title && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please enter a quote title before creating the quote.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default QuoteCreate;
